<template>
  <v-main class="home-page">
    <v-container class="pa-6">
      <!-- Welcome Header -->
      <div class="text-center mb-8">
        <h1 class="text-h3 font-weight-bold text-primary mb-2">Practice Planner</h1>
        <p class="text-h6 text-medium-emphasis">Organize your team's training sessions</p>
      </div>
      <v-row class="gutter-4">
        <!-- Left Column -->
        <v-col cols="12" lg="6">
          <v-row class="gutter-4">
            <!-- Upcoming Practice Plans -->
            <v-col cols="12">
              <v-card class="modern-card" elevation="2">
                <v-card-title class="card-header">
                  <v-icon class="me-3" color="primary">mdi-calendar-clock</v-icon>
                  Upcoming Practice Plans
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-progress-linear
                    :active="loadingRecentPlans"
                    color="primary"
                    height="3"
                    indeterminate
                    class="mb-4"
                  ></v-progress-linear>

                  <div v-if="!loadingRecentPlans && plans.length === 0" class="text-center py-8">
                    <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-calendar-plus</v-icon>
                    <p class="text-body-1 text-medium-emphasis">No upcoming plans</p>
                    <p class="text-body-2 text-disabled">Create your first practice plan</p>
                  </div>

                  <v-list v-else class="plan-list">
                    <v-list-item
                      v-for="plan in plans"
                      :key="plan.id"
                      class="plan-item"
                      :to="{ name: 'planview', params: { id: plan.id } }"
                    >
                      <template v-slot:prepend>
                        <v-avatar color="primary" size="40" class="me-3">
                          <v-icon color="white">mdi-soccer</v-icon>
                        </v-avatar>
                      </template>
                      <v-list-item-title class="text-body-1 font-weight-medium">
                        {{ plan.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-body-2">
                        <DateTimeDisplay :time="plan.date" class="me-2"></DateTimeDisplay>
                        <v-chip size="small" color="primary" variant="outlined">
                          {{ plan.team.name }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    :to="{ name: 'planedit', params: { id: 'new' } }"
                    color="primary"
                    variant="elevated"
                    prepend-icon="mdi-plus"
                  >
                    New Plan
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>

            <!-- My Teams -->
            <v-col cols="12">
              <v-card class="modern-card" elevation="2">
                <v-card-title class="card-header">
                  <v-icon class="me-3" color="success">mdi-account-group</v-icon>
                  My Teams
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-progress-linear
                    :active="loadingTeams"
                    color="success"
                    height="3"
                    indeterminate
                    class="mb-4"
                  ></v-progress-linear>

                  <div v-if="!loadingTeams && teams.length === 0" class="text-center py-8">
                    <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-account-plus</v-icon>
                    <p class="text-body-1 text-medium-emphasis">No teams yet</p>
                    <p class="text-body-2 text-disabled">Create your first team</p>
                  </div>

                  <v-list v-else class="team-list">
                    <v-list-item
                      v-for="team in teams"
                      :key="team.id"
                      class="team-item"
                      :to="{ name: 'teamedit', params: { id: team.id } }"
                    >
                      <template v-slot:prepend>
                        <v-avatar color="success" size="40" class="me-3">
                          <v-icon color="white">mdi-account-group</v-icon>
                        </v-avatar>
                      </template>
                      <v-list-item-title class="text-body-1 font-weight-medium">
                        {{ team.name }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    v-if="canAddTeam"
                    :to="{ name: 'teamedit', params: { id: 'new' } }"
                    color="success"
                    variant="elevated"
                    prepend-icon="mdi-plus"
                  >
                    New Team
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
            <!-- Search -->
            <v-col cols="12">
              <v-card class="modern-card" elevation="2">
                <v-card-title class="card-header">
                  <v-icon class="me-3" color="info">mdi-magnify</v-icon>
                  Search Plans
                </v-card-title>
                <v-card-text class="pa-4">
                  <v-text-field
                    v-model="searchText"
                    label="Search practice plans..."
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    class="mb-4"
                    @keyup.enter="onSearchClick"
                  ></v-text-field>

                  <v-progress-linear
                    :active="isSearching"
                    color="info"
                    height="3"
                    indeterminate
                    class="mb-4"
                  ></v-progress-linear>

                  <div v-if="!isSearching && searchPlans.length === 0 && searchText" class="text-center py-4">
                    <v-icon size="32" color="grey-lighten-1" class="mb-2">mdi-magnify-remove</v-icon>
                    <p class="text-body-2 text-medium-emphasis">No results found</p>
                  </div>

                  <v-list v-if="searchPlans.length > 0" class="search-list">
                    <v-list-item
                      v-for="plan in searchPlans"
                      :key="plan.id"
                      class="search-item"
                      :to="{ name: 'planview', params: { id: plan.id } }"
                    >
                      <template v-slot:prepend>
                        <v-avatar color="info" size="36" class="me-3">
                          <v-icon color="white" size="small">mdi-file-document</v-icon>
                        </v-avatar>
                      </template>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        {{ plan.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        <DateTimeDisplay :time="plan.date"></DateTimeDisplay>
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
        <!-- Right Column - Calendar -->
        <v-col cols="12" lg="6">
          <v-card class="modern-card calendar-card" elevation="2">
            <v-card-title class="card-header">
              <v-icon class="me-3" color="purple">mdi-calendar-month</v-icon>
              Calendar View
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="mb-4">
                <VueDatePicker v-model="selectedMonth" month-picker class="date-picker"/>
              </div>
              
              <v-progress-linear
                :active="loadingDayPlans"
                color="purple"
                height="3"
                indeterminate
                class="mb-4"
              ></v-progress-linear>
              <div v-if="!loadingDayPlans && events.length === 0" class="text-center py-8">
                <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-calendar-blank</v-icon>
                <p class="text-body-1 text-medium-emphasis">No plans this month</p>
                <p class="text-body-2 text-disabled">Create a practice plan to get started</p>
              </div>

              <v-list v-else class="calendar-list">
                <v-list-item
                  v-for="plan in events"
                  :key="plan.id"
                  class="calendar-item"
                  :to="{ name: 'planview', params: { id: plan.id } }"
                >
                  <template v-slot:prepend>
                    <v-avatar :color="plan.color" size="40" class="me-3">
                      <v-icon color="white">mdi-calendar-check</v-icon>
                    </v-avatar>
                  </template>
                  <v-list-item-title class="text-body-1 font-weight-medium">
                    {{ plan.plan.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-body-2">
                    <DateTimeDisplay :id="plan.id" :time="plan.plan.date" class="me-2"></DateTimeDisplay>
                    <v-chip size="small" :color="plan.color" variant="outlined">
                      {{ plan.plan.team.name }}
                    </v-chip>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="plan.plan.notes" class="text-caption mt-1">
                    {{ plan.plan.notes }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import {ref} from 'vue';
import {currentUserStore} from "@/stores/currentUserStore";
import api from '../api'
import DateTimeDisplay from './components/DateTimeDisplay.vue'
import {VCalendar} from 'vuetify/labs/VCalendar'

const defaultColors = ['blue', 'indigo', 'deep-purple', 'cyan', 'green', 'orange', 'grey darken-1'];

export default {
  name: 'HomePage',
  components: {
    DateTimeDisplay
  },
  data() {

    return {
      selectedMonth: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
      },
      loadingRecentPlans: true,
      loadingDayPlans: true,
      loadingTeams: true,
      canAddTeam: false,
      user: ref({}),
      plans: [],
      dayPlans: [],
      searchText: "",
      isSearching: false,
      searchPlans: [],
      calendarMonth: null,
      events: [],
    }
  },
  created() {
    this.$watch(
        () => this.selectedMonth,
        () => {
          this.loadDayPlans()
        },
        // fetch the data when the view is created and the data is
        // already being observed
        {immediate: true}
    )
  },
  mounted() {
    let self = this;
    this.user = currentUserStore().currentUser;
    this.setup();
    currentUserStore().$subscribe(() => {
      self.user = currentUserStore().currentUser;
      this.setup();
    })
  },

  methods: {
    onSearchClick() {
      this.isSearching = true;
      console.log(this.searchText)

      api.practicePlans.search(this.searchText, (plans) => {
        this.searchPlans = plans;
        this.isSearching = false;
      })
    },
    loadDayPlans() {
      this.loadingDayPlans = true;

      let startDate = new Date(this.selectedMonth.year, this.selectedMonth.month, 1)
      console.log("selected day changed", startDate)

      if (startDate[0]) {
        startDate = new Date(startDate[0])
      }

      startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

      let endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

      if (!endDate) {
        return;
      }

      endDate.setDate(endDate.getDate() + 1);

      api.practicePlans.getRange(startDate, endDate, (plans) => {
        let teamNames = [];
        this.dayPlans = plans;

        this.events = [];

        //setup color map
        plans.forEach((practice) => {
          if (teamNames.indexOf(practice.team.id) === -1) {
            teamNames.push(practice.team.id);
          }
        });
        teamNames = teamNames.sort();

        plans.forEach((practice) => {
          this.events.push({
            title: practice.name,
            id: practice.id,
            plan: practice,
            color: defaultColors[teamNames.indexOf(practice.team.id) % defaultColors.length]
          })
        });

        this.loadingDayPlans = false;
      })
    },
    setup() {
      this.error = null;

      if (!this.user.id) {
        return;
      }

      this.loadingTeams = false;
      this.teams = this.user.teams;

      if (this.user.subscription === "basic" && this.teams.length < 1) {
        this.canAddTeam = true;
      } else if (this.user.subscription !== "basic") {
        this.canAddTeam = true;
      }

      api.practicePlans.listRecent((recent, err) => {
        this.loadingRecentPlans = false;


        if (err) {
          this.error = err.toString()
        } else {
          this.plans = recent
        }
      })


    },
  },

}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* Modern card styling */
.modern-card {
  border-radius: 16px !important;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.modern-card:hover {
  transform: none;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-weight: 600;
  padding: 20px 24px;
}

/* List styling */
.plan-list .plan-item,
.team-list .team-item,
.search-list .search-item,
.calendar-list .calendar-item {
  border-radius: 12px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
}

.plan-list .plan-item:hover,
.team-list .team-item:hover,
.search-list .search-item:hover,
.calendar-list .calendar-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  transform: none;
}

/* Avatar styling */
.v-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Button styling */
.v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
}

/* Progress bar styling */
.v-progress-linear {
  border-radius: 2px !important;
}

/* Date picker styling */
.date-picker {
  border-radius: 12px;
  overflow: hidden;
}

/* Calendar card specific */
.calendar-card {
  min-height: 600px;
}

/* Empty state styling */
.text-center.py-8 {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  margin: 16px 0;
}

/* Chip styling */
.v-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

/* Text field styling */
.v-text-field .v-field {
  border-radius: 12px !important;
}

/* Hide calendar weeks */
.vc-weeks {
  display: none !important;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .modern-card {
    margin-bottom: 16px;
  }
  
  .card-header {
    padding: 16px 20px;
  }
}

/* Animation for list items */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.plan-item,
.team-item,
.search-item,
.calendar-item {
  animation: slideIn 0.3s ease-out;
}

/* Hover effects for interactive elements */
.v-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.04) !important;
}

/* Custom scrollbar for lists */
.v-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.v-list::-webkit-scrollbar {
  width: 6px;
}

.v-list::-webkit-scrollbar-track {
  background: transparent;
}

.v-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.v-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}
</style>
    